import type {
  AdminApiFunnel,
  FunnelUpdatePayload,
} from "@repo/app-types/api/funnel";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useState, type FC } from "react";
import { Dialog } from "react-aria-components";
import { useUpdateFunnelMutation } from "../../../api/entities/funnel.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { FunnelSettingsFields } from "./funnelSettingsFields.js";

function checkSamePlaybookIds(
  a: readonly PlaybookId[],
  b: readonly PlaybookId[],
): boolean {
  const bSet = new Set(b);
  return a.length === b.length && a.every((id) => bSet.has(id));
}

export interface FunnelSettingsModalProps {
  funnel: AdminApiFunnel;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/** Edits a generated funnel's selling strategy and playbooks. */
export const FunnelSettingsModal: FC<FunnelSettingsModalProps> = (props) => {
  const { funnel, isOpen, onOpenChange } = props;

  return (
    <CenteredModal
      isDismissable
      isOpen={isOpen}
      size={Size.LARGE}
      onOpenChange={onOpenChange}
    >
      <Dialog className="w-full px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
        {/* Mounted per open, so drafts start from the saved funnel. */}
        {({ close }) => <FunnelSettingsForm funnel={funnel} onClose={close} />}
      </Dialog>
    </CenteredModal>
  );
};

const FunnelSettingsForm: FC<{
  funnel: AdminApiFunnel;
  onClose: () => void;
}> = (props) => {
  const { funnel, onClose } = props;

  const [sellingStrategy, setSellingStrategy] = useState(
    funnel.selling_strategy,
  );
  const [playbookIds, setPlaybookIds] = useState<readonly PlaybookId[]>(
    funnel.playbook_ids,
  );
  const [updateFunnel, { isLoading: isSaving }] = useUpdateFunnelMutation();
  const canEdit = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.TEAMMATES,
  );

  const isStrategyChanged = sellingStrategy !== funnel.selling_strategy;
  const arePlaybooksChanged = !checkSamePlaybookIds(
    playbookIds,
    funnel.playbook_ids,
  );

  const handleSave = async () => {
    const payload: FunnelUpdatePayload = {
      sellingStrategy: isStrategyChanged ? sellingStrategy : undefined,
      playbookIds: arePlaybooksChanged ? [...playbookIds] : undefined,
    };
    try {
      await updateFunnel({ funnelId: funnel.id, payload }).unwrap();
      showToast({
        variant: ToastVariant.Success,
        title: "Funnel saved",
      });
      onClose();
    } catch (error) {
      console.error("Failed to update funnel:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Couldn't save the funnel",
        description: "Please try again.",
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ModalHeader isCloseVisible>Selling strategy</ModalHeader>
      <p className="text-sm text-muted-foreground">
        Interactions labeled after a change to the selling strategy see the new
        text. An added playbook&apos;s top reps are labeled in the background.
      </p>
      <FunnelSettingsFields
        sellingStrategy={sellingStrategy}
        onSellingStrategyChange={setSellingStrategy}
        playbookIds={playbookIds}
        onPlaybookIdsChange={setPlaybookIds}
        isDisabled={!canEdit || isSaving}
      />
      <div className="flex justify-end gap-2">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={onClose}
          isDisabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={() => void handleSave()}
          isLoading={isSaving}
          isDisabled={!canEdit || (!isStrategyChanged && !arePlaybooksChanged)}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
